/**
 * Состояние списков произведений/архива.
 * Источник истины — sessionStorage (один JSON на список).
 * Сброс только при переходе в другой раздел меню.
 */

const ROWS_OPTIONS = [20, 50, 100];
const VIEW_MODES = ['table', 'cards'];

export const ARTWORKS_LIST_KEY = 'artworks_list_view_v2';
export const ARCHIVE_LIST_KEY = 'archive_list_view_v2';

const DEFAULT_STATE = {
  page: 0,
  rowsPerPage: 20,
  viewMode: 'table',
  search: '',
  searchInput: '',
  collectionFilter: '',
  statusFilter: '',
  scrollY: 0,
};

function safeParse(raw) {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function loadListViewState(storageKey) {
  try {
    const parsed = safeParse(sessionStorage.getItem(storageKey));
    if (!parsed || typeof parsed !== 'object') {
      return { ...DEFAULT_STATE };
    }
    const rowsPerPage = ROWS_OPTIONS.includes(parsed.rowsPerPage)
      ? parsed.rowsPerPage
      : DEFAULT_STATE.rowsPerPage;
    const viewMode = VIEW_MODES.includes(parsed.viewMode)
      ? parsed.viewMode
      : DEFAULT_STATE.viewMode;
    const page = Number.isFinite(parsed.page) && parsed.page >= 0 ? parsed.page : 0;
    const search = typeof parsed.search === 'string' ? parsed.search : '';
    const searchInput =
      typeof parsed.searchInput === 'string' && parsed.searchInput !== ''
        ? parsed.searchInput
        : search;
    return {
      page,
      rowsPerPage,
      viewMode,
      search,
      searchInput,
      collectionFilter:
        typeof parsed.collectionFilter === 'string' ? parsed.collectionFilter : '',
      statusFilter: typeof parsed.statusFilter === 'string' ? parsed.statusFilter : '',
      scrollY: Number.isFinite(parsed.scrollY) && parsed.scrollY >= 0 ? parsed.scrollY : 0,
    };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

export function saveListViewState(storageKey, state) {
  try {
    const current = loadListViewState(storageKey);
    const next = {
      page: state.page !== undefined ? state.page : current.page,
      rowsPerPage: state.rowsPerPage !== undefined ? state.rowsPerPage : current.rowsPerPage,
      viewMode: state.viewMode !== undefined ? state.viewMode : current.viewMode,
      search: state.search !== undefined ? state.search : current.search,
      searchInput: state.searchInput !== undefined ? state.searchInput : current.searchInput,
      collectionFilter:
        state.collectionFilter !== undefined ? state.collectionFilter : current.collectionFilter,
      statusFilter: state.statusFilter !== undefined ? state.statusFilter : current.statusFilter,
      scrollY: state.scrollY !== undefined ? state.scrollY : current.scrollY,
    };
    sessionStorage.setItem(storageKey, JSON.stringify(next));
    return next;
  } catch {
    return state;
  }
}

export function clearListViewState(storageKey) {
  try {
    sessionStorage.removeItem(storageKey);
    // Старые ключи на случай миграции
    if (storageKey === ARTWORKS_LIST_KEY) {
      [
        'artworks_list_page',
        'artworks_list_rowsPerPage',
        'artworks_list_viewMode',
        'artworks_list_search',
        'artworks_list_searchInput',
        'artworks_list_collectionFilter',
        'artworks_list_statusFilter',
        'artworks_list_scroll',
      ].forEach((k) => sessionStorage.removeItem(k));
    }
    if (storageKey === ARCHIVE_LIST_KEY) {
      [
        'archive_list_page',
        'archive_list_rowsPerPage',
        'archive_list_viewMode',
        'archive_list_search',
        'archive_list_searchInput',
        'archive_list_collectionFilter',
        'archive_list_statusFilter',
        'archive_list_scroll',
      ].forEach((k) => sessionStorage.removeItem(k));
    }
  } catch {
    // ignore
  }
}

export function getArtworksListPath() {
  const state = loadListViewState(ARTWORKS_LIST_KEY);
  const params = new URLSearchParams();
  if (state.search) params.set('q', state.search);
  if (state.collectionFilter) params.set('collection', state.collectionFilter);
  if (state.statusFilter) params.set('status', state.statusFilter);
  if (state.page > 0) params.set('page', String(state.page));
  if (state.rowsPerPage !== 20) params.set('rows', String(state.rowsPerPage));
  if (state.viewMode !== 'table') params.set('view', state.viewMode);
  const qs = params.toString();
  return qs ? `/artworks?${qs}` : '/artworks';
}

/** Переход к списку произведений с заданным поисковым запросом (например #тег). */
export function navigateToArtworksSearch(navigate, search) {
  const text = typeof search === 'string' ? search.trim() : '';
  saveListViewState(ARTWORKS_LIST_KEY, {
    ...loadListViewState(ARTWORKS_LIST_KEY),
    search: text,
    searchInput: text,
    page: 0,
    scrollY: 0,
  });
  navigate('/artworks');
}

export function isArtworksSection(pathname) {
  return pathname === '/artworks' || pathname.startsWith('/artworks/');
}

export function isArchiveSection(pathname) {
  return pathname === '/archive' || pathname.startsWith('/archive/');
}

export function clearListStateOnSectionChange(fromPathname, toPath) {
  if (isArtworksSection(fromPathname) && !isArtworksSection(toPath)) {
    clearListViewState(ARTWORKS_LIST_KEY);
  }
  if (isArchiveSection(fromPathname) && !isArchiveSection(toPath)) {
    clearListViewState(ARCHIVE_LIST_KEY);
  }
}

export { ROWS_OPTIONS, DEFAULT_STATE };
